/**
 * Heuristic mapper from a Pathogen catalog record to a cultural-risk
 * PathogenClass (transmission-based). The catalog's `pathogenClass` is
 * taxonomic (virus/bacterium/parasite/fungus/prion) — the cultural-risk
 * register hangs off TRANSMISSION mode (filovirus / respiratory-droplet
 * / faecal-oral / vector-borne / STI-BBV / zoonotic-spillover /
 * healthcare-associated) since cultural factors track transmission
 * route, not taxonomy.
 *
 * Falls back to no-class when the pathogen doesn't match a known
 * transmission family. v1 covers the catalog's currently-wired species;
 * v2 should derive this from the pathogen record's `primaryRoute` +
 * `vectorIds` + `taxonomy.family` instead of a hand-mapped lookup.
 */

import type { PathogenClass } from '@biomax/plugin-region-database';

/** Map pathogen catalog id → cultural-risk PathogenClass. */
export function pathogenToCulturalClass(pathogenId: string): PathogenClass | undefined {
  // Filovirus + arenavirus VHF — burial-practice cultural factors trigger here
  if (/^(ebola|marburg|lassa|cchf|rift-valley|hantavirus)/.test(pathogenId)) {
    return 'filovirus';
  }
  // Respiratory droplet + airborne
  if (/^(sars-cov|influenza|mers|measles|mumps|rubella|rsv|tuberculosis|meningococcus|streptococcus-pneumoniae)/.test(pathogenId)) {
    return 'respiratory-droplet';
  }
  // Faecal-oral
  if (/^(cholera|hepatitis-a|hepatitis-e|norovirus|polio|typhoid|shigella|salmonella-non-typhi|rotavirus)/.test(pathogenId)) {
    return 'faecal-oral';
  }
  // Vector-borne (mosquito / tick / midge)
  if (/^(dengue|malaria|zika|chikungunya|yellow-fever|oropouche|west-nile|japanese-encephalitis|tick)/.test(pathogenId)) {
    return 'vector-borne';
  }
  // STI / blood-borne
  if (/^(gonorrhoeae|syphilis|hiv|hepatitis-b|hepatitis-c|mpox)/.test(pathogenId)) {
    return 'STI-BBV';
  }
  // Healthcare-associated + AMR
  if (/(amr|mdr|mrsa|candida-auris|clostridioides-difficile|acinetobacter)/.test(pathogenId)) {
    return 'healthcare-associated';
  }
  // Zoonotic spillover (animal-interface)
  if (/^(plague|nipah|rabies|brucellosis|q-fever|melioidosis|smallpox)/.test(pathogenId)) {
    return 'zoonotic-spillover';
  }
  return undefined;
}
